import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDownIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export interface SelectOption {
  readonly label: string;
  readonly value: string;
  readonly disabled?: boolean;
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'children' | 'size'
> {
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  readonly invalid?: boolean;
  readonly size?: 'sm' | 'md' | 'lg';
}

const SIZE = {
  sm: 'h-[34px] text-[12.5px] ps-3 pe-9',
  md: 'h-10 text-[13.5px] ps-3.5 pe-10',
  lg: 'h-12 text-[15px] ps-4 pe-12',
};
const ICON_INSET = { sm: 'end-2', md: 'end-3', lg: 'end-3.5' };

/**
 * Native HTML <select> styled to match the design system. Use for short
 * single-choice lists where keyboard / iOS-native picker behaviour is wanted
 * for free. For searchable / multi-select / large datasets, use Combobox.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, invalid, size = 'md', className, disabled, ...rest },
  ref,
) {
  const Trailing: ReactNode = (
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-y-0 flex items-center text-ink-3',
        ICON_INSET[size],
      )}
    >
      <ChevronDownIcon size={16} color="currentColor" />
    </span>
  );
  return (
    <div className={cn('relative inline-flex w-full', disabled && 'opacity-45')}>
      <select
        ref={ref}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full min-w-0 appearance-none rounded-lg bg-surface text-ink [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:text-base',
          'border border-ink-3/80',
          'transition-[box-shadow,border-color] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
          'focus:outline-2 focus:outline-offset-2 focus:outline-accent-text focus:border-accent-text',
          'disabled:cursor-not-allowed',
          invalid && 'border-danger focus:border-danger focus:ring-danger/25',
          SIZE[size],
          className,
        )}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {Trailing}
    </div>
  );
});
